/**
 * Optical address proposals, and the rules that keep them proposals.
 *
 * Deliberately separate from `opticalClientForm.ts`, which has a `values` bag the form applies
 * without asking when a field is empty. That is right for a machine-readable-zone field: its check
 * digits make it arithmetically believable. It is wrong for an address read off printed text, which
 * nothing validates. So there is no auto-apply path here at all - not for an empty form, not at any
 * confidence. Every component starts unticked and stays unticked until a person chooses it.
 *
 * No value read from a document may be logged from this module.
 */

import type { ClientFormValues } from './idCardClientForm';
import { readCalibrations, type CaptureCalibration } from './opticalCalibration';
import type { OpticalScanResult } from './opticalScan';

/** A component the service can propose, as the wire spells it. */
export type OpticalAddressField =
    | 'county'
    | 'locality'
    | 'commune'
    | 'village'
    | 'street'
    | 'streetNumber'
    | 'building'
    | 'staircase'
    | 'floor'
    | 'apartment'
    | 'postalCode';

/** One component, as the service proposed it. */
export interface OpticalAddressComponent {
    readonly field: OpticalAddressField;
    readonly value: string;
    readonly label: string | null;
}

/** What the service said about the printed address block. */
export interface OpticalAddressProposalResponse {
    readonly outcome: 'PROPOSED' | 'ADDRESS_NOT_EXTRACTED';
    readonly reasonCode: string | null;
    readonly components: readonly OpticalAddressComponent[];
    readonly templateReference: string | null;
    readonly provider: string | null;
    /** Whether the provider reports usable confidence. False for the Windows engine. */
    readonly confidenceAvailable: boolean;
    /** True whenever confidence is unavailable. Stated by the service, not inferred here. */
    readonly requiresManualVerification: boolean;
    /**
     * Where ink is on this capture, present only while the reader has no calibrated address
     * rectangle. Geometry only - it says nothing about what any band contains.
     *
     * **Nullable as well as optional, because the service sends both.** The Host writes nulls
     * rather than omitting them, so this property arrives as `null` on every scan that measured no
     * geometry - which is most of them. Typing it `?:` alone said "absent or a calibration" and the
     * page believed it: a `!== undefined` guard let `null` through to a panel that reads `.bands`
     * off it, and on 2026-09-14 the whole application went white in front of an operator. The three
     * states are distinct and all three are real - absent means an older service, null means this
     * one measured nothing, an object means the operator has something to act on.
     */
    readonly calibration?: CaptureCalibration | null;
    /**
     * Every illumination this scan measured, one entry each, unranked.
     *
     * The member a current page reads. `calibration` above can only ever describe one source, and
     * the question a calibration run exists to answer is which of two is better - so a single
     * measurement cannot answer it however well formed it is.
     */
    readonly calibrations?: readonly CaptureCalibration[] | null;
    /**
     * Shape version of those entries.
     *
     * Absent or zero means a service that predates the collection. A page that does not recognise
     * the version it is shown must fail closed rather than read geometry it may be misinterpreting.
     */
    readonly calibrationSchemaVersion?: number | null;
    /**
     * What the address pass was handed, as bounded facts about the capture.
     *
     * Diagnostic. A data type name, a byte count, a container label and a four-byte file
     * signature - so a scan that proposed nothing can still be explained afterwards, which the one
     * on 2026-09-11 could not. Never rendered as a value; never carries document content.
     */
    readonly image?: OpticalCaptureImageFacts | null;
}

/** Bounded facts about the capture an address pass was given. Diagnostic only. */
export interface OpticalCaptureImageFacts {
    readonly requestedDataType: string;
    readonly byteLength: number;
    readonly format: string;
    readonly signature: string;
    readonly decoded: boolean;
    /** The container the reader says it was configured to return, read back from its settings. */
    readonly configuredFormat?: string | null;
    /** Whether that configured container and the one the bytes actually carry are the same. */
    readonly formatsAgree?: boolean | null;
}

/**
 * The form fields an address component can reach.
 *
 * Six of eleven. `commune`, `village`, `building`, `staircase` and `floor` are genuinely printed on
 * the card and genuinely read, and the client record has nowhere to put them - so they are shown as
 * read-but-not-storable rather than quietly dropped at save, which is the worse of the two failures.
 *
 * `commune` and `village` have one further path, and only together: see `RURAL_LOCALITY_FIELD`.
 */
const FORM_FIELD: Partial<Record<OpticalAddressField, keyof ClientFormValues>> = {
    county: 'jud',
    locality: 'oras',
    street: 'str',
    streetNumber: 'nr',
    apartment: 'ap',
    postalCode: 'codPostal',
};

/**
 * The one item this page composes rather than reads: a locality built from a rural card's village
 * and commune.
 *
 * A rural Romanian card prints no `Mun.`/`Ors.` locality at all; it prints `Sat. <village>
 * (Com. <commune>)`, and the client record has one locality field. The legacy application stored
 * exactly `<village> (Com. <commune>)` in that field for one client in five - a fact established
 * by aggregate counts on the seeded database on 2026-09-16, not by inspection of any value - so
 * that is the form composed here, and the only one. It targets `oras` and nothing else.
 *
 * Composed only when **both** components were read. A village without its commune, or a commune
 * without its village, is left visible and unstorable: no legacy representation of either alone
 * was proven (the rows that carry one alone cannot be told from rows where the other was lost),
 * and inventing the missing half would be a guess dressed as a reading.
 */
export const RURAL_LOCALITY_FIELD = 'ruralLocality' as const;

/** A row the review can show: a component as read, or the one composed locality. */
export type OpticalAddressReviewField = OpticalAddressField | typeof RURAL_LOCALITY_FIELD;

/** Where a review item came from. */
export type OpticalAddressReviewRole =
    /** Read off the card, as one labelled value. */
    | 'direct'
    /** Composed on this page from read components. Never read as one value. */
    | 'derived'
    /** Read off the card, and feeding a derived item - shown as evidence, never stored itself. */
    | 'source';

/** Why an item that the record could store must still not be applied. */
export type OpticalAddressBlock =
    /**
     * The card yielded a direct locality *and* a village-with-commune, which compose a different
     * one. Two readings of one field is a contradiction, not a choice, and neither is applied.
     */
    'localityContradiction';

/** The exact legacy form, and the only composition this page performs. */
function composeRuralLocality(village: string, commune: string): string {
    return `${village} (Com. ${commune})`;
}

/**
 * Every component this pipeline can propose, in the order a person reads an address.
 *
 * Ordered and exhaustive because the section has to be able to say what was *not* read. A list of
 * what came back cannot distinguish a component the card does not carry from one the reader failed
 * on, and the operator needs to see the difference rather than infer it from an absence.
 */
export const ADDRESS_COMPONENTS: readonly OpticalAddressField[] = [
    'county',
    'locality',
    'commune',
    'village',
    'street',
    'streetNumber',
    'building',
    'staircase',
    'floor',
    'apartment',
    'postalCode',
];

/**
 * What the address section is showing, as one value a screen can switch on.
 *
 * A state rather than a set of booleans, because the section must render in *every* completed scan
 * and each state says something different to the operator. Before this existed the section simply
 * vanished when there was nothing to offer, which reads as "this feature does not exist here"
 * rather than as the specific, answerable thing that actually happened.
 */
export type OpticalAddressState =
    /** Components were read and are on offer, unticked. */
    | 'proposed'
    /** The reader has no measured crop rectangle for this card yet. The operator can fix this. */
    | 'calibrationRequired'
    /** No visible-light front image reached the address pass, so no OCR ran. */
    | 'visibleImageUnavailable'
    /**
     * The template names an illumination this scan did not supply, or names none at all.
     *
     * A statement about configuration, not about the card. It exists because an infrared-calibrated
     * template handed only a visible frame must fail closed rather than crop the frame it happens to
     * have - and the operator needs to be told which of the two problems it is.
     */
    | 'sourceUnavailable'
    /** The pass ran and no component survived it. */
    | 'notExtracted'
    /** This workstation's service says nothing about addresses at all. */
    | 'unsupported'
    /**
     * A current service produced no usable address result.
     *
     * Distinct from `unsupported`, which means the service predates the feature. This one means the
     * feature is installed and its answer did not arrive - a statement about the service, not about
     * the card and not about the operator's equipment. On 2026-09-14 a current service produced
     * exactly this and the page called it an old build.
     */
    | 'contractUnavailable';

/** What an operator is being asked to decide about one component. */
export interface OpticalAddressReviewItem {
    readonly field: OpticalAddressReviewField;
    /** The form field it would reach, or null when the record cannot store it. */
    readonly formField: keyof ClientFormValues | null;
    readonly proposed: string;
    /** What the form holds now. Empty string when it holds nothing. */
    readonly current: string;
    /** Whether accepting would replace something the operator already has. */
    readonly conflict: boolean;
    /** Whether the record can store it at all. */
    readonly storable: boolean;
    /** Read, composed, or read-and-feeding-a-composition. */
    readonly role: OpticalAddressReviewRole;
    /** The read components a derived item was composed from. Empty for anything read directly. */
    readonly derivedFrom: readonly OpticalAddressField[];
    /** Why a storable item still cannot be applied, or null. */
    readonly blocked: OpticalAddressBlock | null;
    /**
     * Whether ticking it can move a value at all: storable and not blocked. The only predicate
     * `applyOpticalAddress` consults, so a screen and the apply path cannot disagree.
     */
    readonly applicable: boolean;
}

/** Everything a screen needs in order to present an address proposal honestly. */
export interface OpticalAddressReview {
    readonly available: boolean;
    /** Which of the five things happened. The section renders from this, never from `available`. */
    readonly state: OpticalAddressState;
    /** Stable code explaining why nothing is on offer. Null when something is. */
    readonly reasonCode: string | null;
    readonly items: readonly OpticalAddressReviewItem[];
    /**
     * Components this pipeline supports that this scan did not read.
     *
     * Listed so a partial read is visibly partial. Nothing is guessed at to fill them: a component
     * the reader did not anchor to a printed label stays absent, and saying so is the whole point.
     */
    readonly missing: readonly OpticalAddressField[];
    readonly confidenceAvailable: boolean;
    readonly requiresManualVerification: boolean;
    readonly provider: string | null;
    readonly templateReference: string | null;
    /**
     * Every illumination the scan measured, already filtered to entries that can be drawn.
     *
     * Empty when the service sent none, when it sent a schema this page does not know, or when what
     * it sent did not survive the structural guard. All three are the same thing to a screen: there
     * is nothing safe to put a rectangle over.
     */
    readonly calibrations: readonly CaptureCalibration[];
}

/**
 * The calibration schema this page understands.
 *
 * A service announcing a different one is not read. Geometry whose meaning may have changed is
 * worse than no geometry: it draws a rectangle over an identity document that looks authoritative
 * and is measured against something else.
 */
export const SUPPORTED_CALIBRATION_SCHEMA = 1;

/** The measured sources a page may draw, or none. */
function calibrationsOf(
    proposal: OpticalAddressProposalResponse | null | undefined,
): readonly CaptureCalibration[] {
    if (!proposal || proposal.calibrationSchemaVersion !== SUPPORTED_CALIBRATION_SCHEMA) {
        return [];
    }

    return readCalibrations(proposal.calibrations);
}

/** Which state a reason code puts the section in. */
function stateFor(reasonCode: string | null): OpticalAddressState {
    if (reasonCode === null) {
        return 'unsupported';
    }

    // The service saying "my own address result went missing". It is never an extraction outcome:
    // nothing about the document caused it, and nothing about the document will fix it.
    if (reasonCode === 'ADDRESS_PIPELINE_UNAVAILABLE') {
        return 'contractUnavailable';
    }

    if (reasonCode === 'CAPTURE_GEOMETRY_UNCALIBRATED') {
        return 'calibrationRequired';
    }

    // Its own state, not a generic failure. A reader that is installed and working but was handed
    // no picture of the card front is a different problem from one that ran and read nothing, and
    // it must never be shown as the reader being absent.
    if (reasonCode === 'VISIBLE_FRONT_IMAGE_UNAVAILABLE') {
        return 'visibleImageUnavailable';
    }

    // Configuration, not capture. Reported apart from the two above because the fix is a template
    // change or a reader setting, and nothing about the card or the operator's handling caused it.
    if (
        reasonCode === 'INFRARED_FRONT_IMAGE_UNAVAILABLE' ||
        reasonCode === 'ADDRESS_SOURCE_NOT_CONFIGURED' ||
        reasonCode === 'CONFIGURED_ADDRESS_SOURCE_UNAVAILABLE'
    ) {
        return 'sourceUnavailable';
    }

    return 'notExtracted';
}

/** Nothing to review, for a stated reason. */
function nothing(
    reasonCode: string | null,
    calibrations: readonly CaptureCalibration[] = [],
): OpticalAddressReview {
    return {
        available: false,
        state: stateFor(reasonCode),
        reasonCode,
        items: [],
        // Nothing was read, so nothing is listed as missing either: naming eleven absent components
        // when the pass never ran would dress a stopped pipeline up as a poor read.
        missing: [],
        confidenceAvailable: false,
        requiresManualVerification: true,
        provider: null,
        templateReference: null,
        calibrations,
    };
}

/**
 * The one code that says what the address pass did, for the audit and for nothing else.
 *
 * A proposal records `PROPOSED` - that a suggestion was offered, not that anyone took it, which is
 * the save's own `applied` flag to assert. Anything else records the service's reason. A service
 * built before the address pass sends no block at all and records nothing, which is stored as "not
 * reported" rather than as a failure.
 *
 * It is a code in every branch. The proposed values never travel with it, even when the operator
 * accepted every one of them.
 *
 * @param result The completed scan, or null.
 */
export function addressAuditOutcome(result: OpticalScanResult | null | undefined): string | null {
    const proposal = result?.addressProposal;

    if (!proposal) {
        return null;
    }

    return proposal.outcome === 'PROPOSED' ? 'PROPOSED' : (proposal.reasonCode ?? proposal.outcome);
}

/**
 * Works out what an address proposal is offering, without applying any of it.
 *
 * @param result The completed scan, or null.
 * @param current What the form holds now. Missing keys count as empty.
 */
export function reviewOpticalAddress(
    result: OpticalScanResult | null | undefined,
    current: Partial<ClientFormValues> = {},
): OpticalAddressReview {
    const proposal = result?.addressProposal;

    if (proposal === null) {
        // Present and null. Only a service that knows about the address contract writes this
        // property at all, so a null here is that service failing to fill it - never an old build.
        // Absence and null are different facts and the page must not collapse them.
        return { ...nothing('ADDRESS_PIPELINE_UNAVAILABLE'), state: 'contractUnavailable' };
    }

    if (proposal === undefined) {
        // The property is missing entirely, which only a service built before this feature does.
        // That is not a failure and must not look like one.
        return nothing(null);
    }

    if (proposal.outcome !== 'PROPOSED' || proposal.components.length === 0) {
        return nothing(proposal.reasonCode ?? 'ADDRESS_NOT_EXTRACTED', calibrationsOf(proposal));
    }

    const items = withRuralLocality(
        proposal.components.map(component =>
            reviewItem(component.field, FORM_FIELD[component.field] ?? null, component.value, current),
        ),
        current,
    );

    // What the operator has something for, judged on the effective items rather than on the raw
    // read: a composed locality stands for the locality. On 2026-09-16 the notice named Locality
    // as unread while the composed row sat directly above it, because this was the raw set.
    const proposedFields = new Set(items.map(coveredField));

    return {
        available: true,
        state: 'proposed',
        reasonCode: null,
        items,
        missing: ADDRESS_COMPONENTS.filter(field => !proposedFields.has(field)),
        confidenceAvailable: proposal.confidenceAvailable,
        requiresManualVerification: proposal.requiresManualVerification,
        provider: proposal.provider,
        templateReference: proposal.templateReference,
        // A scan that proposed components did not calibrate, so there is nothing to draw.
        calibrations: [],
    };
}

/** One review row. */
function reviewItem(
    field: OpticalAddressReviewField,
    formField: keyof ClientFormValues | null,
    proposed: string,
    current: Partial<ClientFormValues>,
    role: OpticalAddressReviewRole = 'direct',
    derivedFrom: readonly OpticalAddressField[] = [],
): OpticalAddressReviewItem {
    const held = formField ? (current[formField] ?? '') : '';
    const storable = formField !== null;

    return {
        field,
        formField,
        proposed,
        current: held,
        conflict: storable && held.trim().length > 0 && held !== proposed,
        storable,
        role,
        derivedFrom,
        blocked: null,
        applicable: storable,
    };
}

/** The wire field a review row stands for: itself, or `locality` for the composed row. */
function coveredField(entry: OpticalAddressReviewItem): OpticalAddressField {
    return entry.field === RURAL_LOCALITY_FIELD ? 'locality' : entry.field;
}

/**
 * Adds the composed rural locality where the card warrants one, and nothing anywhere else.
 *
 * Three outcomes, decided here and nowhere else:
 * - village and commune, no direct locality: one derived `ruralLocality` item, storable to `oras`,
 *   inserted after its two sources, which become `source` rows;
 * - village and commune *and* a direct locality that reads differently: no derived item, and the
 *   direct locality is blocked - shown, with its sources, but not applicable. Two readings of one
 *   field are a contradiction, and the operator types the locality;
 * - anything else: every row exactly as read.
 */
function withRuralLocality(
    items: readonly OpticalAddressReviewItem[],
    current: Partial<ClientFormValues>,
): readonly OpticalAddressReviewItem[] {
    const village = items.find(entry => entry.field === 'village');
    const commune = items.find(entry => entry.field === 'commune');
    if (village === undefined || commune === undefined) {
        return items;
    }

    const composed = composeRuralLocality(village.proposed, commune.proposed);
    const asSource = (entry: OpticalAddressReviewItem): OpticalAddressReviewItem =>
        entry === village || entry === commune ? { ...entry, role: 'source' } : entry;

    const locality = items.find(entry => entry.field === 'locality');
    if (locality !== undefined) {
        if (locality.proposed === composed) {
            return items;
        }

        return items.map(entry =>
            entry === locality
                ? { ...entry, blocked: 'localityContradiction', applicable: false }
                : asSource(entry),
        );
    }

    const derived = reviewItem(
        RURAL_LOCALITY_FIELD,
        'oras',
        composed,
        current,
        'derived',
        ['village', 'commune'],
    );
    const lastSource = Math.max(items.indexOf(village), items.indexOf(commune));

    return [
        ...items.slice(0, lastSource + 1).map(asSource),
        derived,
        ...items.slice(lastSource + 1),
    ];
}

/**
 * Turns the components an operator ticked into form values.
 *
 * An empty selection yields an empty object - the case that matters, because it is what a person
 * who looked at a proposal and chose nothing must produce. An item that is not applicable - the
 * record cannot store it, or it is blocked - is ignored even if it is ticked, so a value can never
 * be accepted into a field that does not exist or over a contradiction.
 *
 * @param review What was offered.
 * @param accepted The rows the operator ticked.
 */
export function applyOpticalAddress(
    review: OpticalAddressReview,
    accepted: readonly OpticalAddressReviewField[],
): Partial<ClientFormValues> {
    if (accepted.length === 0) {
        return {};
    }

    const chosen = new Set(accepted);
    const values: Partial<ClientFormValues> = {};

    for (const entry of review.items) {
        if (!chosen.has(entry.field) || !entry.applicable || entry.formField === null) {
            continue;
        }

        values[entry.formField] = entry.proposed;
    }

    return values;
}
